import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IoTime, IoEye, IoHeart, IoShare } from 'react-icons/io5';

const Blog = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const blogPosts = [
    {
      id: 1,
      title: t('blog.posts.productivity.title'),
      excerpt: t('blog.posts.productivity.excerpt'),
      content: t('blog.posts.productivity.content'),
      date: '2025-07-09',
      readTime: '5',
      views: '1.2k',
      likes: 42,
      category: t('blog.categories.productivity'),
      categoryKey: 'productivity'
    },
    {
      id: 2,
      title: t('blog.posts.features.title'),
      excerpt: t('blog.posts.features.excerpt'),
      content: t('blog.posts.features.content'),
      date: '2025-07-09',
      readTime: '8',
      views: '850',
      likes: 28,
      category: t('blog.categories.features'),
      categoryKey: 'features'
    },
    {
      id: 3,
      title: t('blog.posts.tips.title'),
      excerpt: t('blog.posts.tips.excerpt'),
      content: t('blog.posts.tips.content'),
      date: '2025-07-09',
      readTime: '6',
      views: '650',
      likes: 35,
      category: t('blog.categories.tips'),
      categoryKey: 'tips'
    },
    {
      id: 4,
      title: 'How to Take Notes Quickly Without an Account: The Best Free Online Notepad',
      excerpt: "Let's be honest—ideas strike when we least expect them. Maybe you're in a meeting, on a call, or halfway through reading an article. In those moments, you need a fast, reliable way to take notes without going through the hassle of signing in.",
      content: (
        <div>
          <p>Let's be honest—ideas strike when we least expect them. Maybe you're in a meeting, on a call, or halfway through reading an article. In those moments, you need a fast, reliable way to take notes without going through the hassle of signing in.</p>
          
          <p>That's where a free online notepad comes in—especially one that's anonymous, secure, and doesn't require an account.</p>
          
          <h3>🚀 Why Use an Online Notepad with No Sign Up?</h3>
          
          <p>Here's what makes an anonymous online notepad a must-have productivity tool:</p>
          
          <p><strong>✅ No account needed</strong><br/>
          You can take notes without an account—just open the page and start writing. No registration, no login, no forgotten passwords.</p>
          
          <p><strong>✅ Instant and distraction-free</strong><br/>
          The best online notepad tools are designed with minimalism in mind. No ads, no toolbars, no pop-ups—just a clean space to write your thoughts.</p>
          
          <p><strong>✅ Auto-save functionality</strong><br/>
          Many anonymous notepad tools, like <Link to="/" className="notepad-link">anonlinenotepad.com</Link>, include auto-save. That means your text is automatically stored in your browser, so you won't lose your notes—even if you accidentally close the tab.</p>
          
          <p><strong>✅ Shareable with a link</strong><br/>
          Need to share your notes with someone? Just copy and share the unique link. It's that easy—no exporting, no attachments.</p>
          
          <h3>💡 Best Use Cases for a No Login Note Tool</h3>
          
          <p>A free online notepad can fit into all kinds of workflows. Some real-world examples:</p>
          <ul>
            <li>🧠 Brainstorming ideas: Jot down creative thoughts or new project ideas on the fly.</li>
            <li>📋 Quick to-do lists: Create daily or weekly checklists without setting up a new account.</li>
            <li>📞 Meeting notes: Record key points during a call or Zoom meeting with zero friction.</li>
            <li>✍️ Writing drafts: Use the online notepad as a distraction-free writing space.</li>
            <li>📜 Anonymous journaling: Reflect on your day privately and securely.</li>
          </ul>
          
          <h3>🔍 Recommended: <Link to="/" className="notepad-link">anonlinenotepad.com</Link></h3>
          
          <p>One of the best tools in this space is <Link to="/" className="notepad-link">anonlinenotepad.com</Link>. It's a simple yet powerful online note-taking platform that:</p>
          <ul>
            <li>Requires no login or signup</li>
            <li>Lets you write notes instantly</li>
            <li>Saves your work automatically</li>
            <li>Enables easy sharing through links</li>
            <li>Works across desktop and mobile devices</li>
          </ul>
          
          <p>Whether you're a student, remote worker, writer, or just someone who loves simplicity, this anonymous online notepad is worth bookmarking.</p>
          
          <h3>🔐 A Word on Privacy</h3>
          
          <p>While it's convenient to take notes without an account, always avoid storing sensitive information like passwords or financial details in these tools—especially if you're using a public or shared device.</p>
          
          <h3>✅ Final Thoughts</h3>
          
          <p>In a world overloaded with apps and logins, it's refreshing to use a tool that gets out of your way and just lets you write. Whether you're capturing inspiration or organizing your thoughts, a no sign up notepad like <Link to="/" className="notepad-link">anonlinenotepad.com</Link> offers the speed, simplicity, and flexibility you need.</p>
          
          <p>No account. No clutter. Just notes.</p>
          
          <p>Try it now at <Link to="/" className="notepad-link">anonlinenotepad.com</Link> — and never miss a thought again.</p>
        </div>
      ),
      date: '2025-07-09',
      readTime: '7',
      views: '2.1k',
      likes: 58,
      category: 'How-to Guide',
      categoryKey: 'updates'
    }
  ];

  // 筛选博客文章
  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.categoryKey === selectedCategory);

  // 获取分类统计
  const getCategoryCount = (categoryKey) => {
    if (categoryKey === 'all') return blogPosts.length;
    return blogPosts.filter(post => post.categoryKey === categoryKey).length;
  };

  const categories = [
    { key: 'all', name: 'All Posts', count: getCategoryCount('all') },
    { key: 'productivity', name: t('blog.categories.productivity'), count: getCategoryCount('productivity') },
    { key: 'features', name: t('blog.categories.features'), count: getCategoryCount('features') },
    { key: 'tips', name: t('blog.categories.tips'), count: getCategoryCount('tips') },
    { key: 'updates', name: t('blog.categories.updates'), count: getCategoryCount('updates') }
  ];

  return (
    <div className="blog-container">
      <div className="blog-header">
        <h1 className="blog-title">{t('blog.title')}</h1>
        <p className="blog-subtitle">{t('blog.subtitle')}</p>
      </div>

      <div className="blog-grid">
        {filteredPosts.map((post) => (
          <article key={post.id} className="blog-post-card">
            <div className="post-header">
              <span className="post-category">{post.category}</span>
              <time className="post-date">{post.date}</time>
            </div>
            
            <h2 className="post-title">{post.title}</h2>
            <p className="post-excerpt">{post.excerpt}</p>
            
            <div className="post-content">
              {typeof post.content === 'string' 
                ? post.content.split('\n').map((paragraph, index) => (
                    <p key={index} className="post-paragraph">{paragraph}</p>
                  ))
                : post.content
              }
            </div>
            
            <div className="post-meta">
              <div className="meta-item">
                <IoTime />
                <span>{post.readTime} {t('blog.readTime')}</span>
              </div>
              <div className="meta-item">
                <IoEye />
                <span>{post.views} {t('blog.views')}</span>
              </div>
              <div className="meta-item">
                <IoHeart />
                <span>{post.likes}</span>
              </div>
            </div>
            
            <div className="post-actions">
              <button className="action-button like-button">
                <IoHeart />
                {t('blog.like')}
              </button>
              <button className="action-button share-button">
                <IoShare />
                {t('blog.share')}
              </button>
            </div>
          </article>
        ))}
      </div>

      <div className="blog-sidebar">
        <div className="sidebar-section">
          <h3 className="sidebar-title">{t('blog.about.title')}</h3>
          <p className="sidebar-text">
            {t('blog.about.content.part1')}
            <Link to="/" className="notepad-link">{t('app.title')}</Link>
            {t('blog.about.content.part2')}
          </p>
        </div>
        
        <div className="sidebar-section">
          <h3 className="sidebar-title">{t('blog.categories.title')}</h3>
          <ul className="categories-list">
            {categories.map((category) => (
              <li key={category.key}>
                <button 
                  className={`category-button ${selectedCategory === category.key ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.key)}
                >
                  {category.name} ({category.count})
                </button>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="sidebar-section">
          <h3 className="sidebar-title">{t('blog.newsletter.title')}</h3>
          <p className="sidebar-text">{t('blog.newsletter.description')}</p>
          <div className="newsletter-form">
            <input 
              type="email" 
              placeholder={t('blog.newsletter.placeholder')}
              className="newsletter-input"
            />
            <button className="newsletter-button">{t('blog.newsletter.subscribe')}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;